// import { CartPageWrapper } from '@/components/web/cart/cart-page-wrapper';
import WebLayout from '@/components/web/layouts/web-layout';
import { Head, router, usePage } from '@inertiajs/react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { CheckoutProgress, generateCheckoutSteps } from '@/components/checkout/checkout-progress';
import { OrderSummaryCard } from '@/components/checkout/order-summary-card';
import { CartEmptyState } from '@/components/web/cart/cart-empty-state';
import { CartServiceSelector } from '@/components/web/cart/cart-service-selector';
import { useLoginGate } from '@/contexts/login-gate-provider';
import { useUserGlobalCart } from '@/hooks/use-user-global-cart';
import { calcScopedDiscount } from '@/lib/voucher';
import { type SharedData } from '@/types';
import { isPast } from 'date-fns';

export default function CartPage() {
  return (
    <WebLayout>
      <Head title="Giỏ hàng - MegaTicket" />
      <CartContent />
    </WebLayout>
  );
}

export function CartContent() {
  const { cart, removeFromCart, isFetching, updateCartItem, selectedSessions, lockSlots } = useUserGlobalCart();
  const { auth, appliedVoucher } = usePage<SharedData>().props;
  const { requireLogin } = useLoginGate();

  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);

  const selectedCartItems = cart.filter((item) => selectedItems.has(item.id));
  const selectedTotal = selectedCartItems.reduce((sum, item) => sum + item.total, 0);
  // Scoped to the selection so the cart and checkout summaries never disagree.
  const selectedDiscount = useMemo(() => calcScopedDiscount(selectedCartItems, appliedVoucher), [selectedCartItems, appliedVoucher]);

  useEffect(() => {
    const reloadOnBack = (event: any) => {
      if (event.detail.visit.type === 'back') {
        router.reload({ only: ['cart'] });
      }
    };

    window.addEventListener('inertia:navigate', reloadOnBack);

    return () => {
      window.removeEventListener('inertia:navigate', reloadOnBack);
    };
  }, []);

  const handleItemSelectionChange = (itemId: string, selected: boolean) => {
    setSelectedItems((prevSelectedItems) => {
      const newSelectedItems = new Set(prevSelectedItems);

      if (selected) {
        const item = cart.find((i) => i.id === itemId);
        if (item) {
          const itemSiteCode = item.options?.site_mapping_code;
          const itemDate = item.options?.date;

          const currentSelectedCartItems = cart.filter((item) => newSelectedItems.has(item.id));
          const conflictingItem = currentSelectedCartItems.find(
            (selectedItem) => selectedItem.options?.site_mapping_code !== itemSiteCode || selectedItem.options?.date !== itemDate,
          );

          if (conflictingItem && newSelectedItems.size > 0) {
            toast.error('Chỉ có thể chọn các dịch vụ cùng địa điểm và cùng ngày');
            return prevSelectedItems;
          }
        }

        newSelectedItems.add(itemId);
      } else {
        newSelectedItems.delete(itemId);
      }

      return newSelectedItems;
    });
  };

  const handleBatchSelectionChange = (itemIds: string[], selected: boolean) => {
    setSelectedItems((prevSelectedItems) => {
      const newSelectedItems = new Set(prevSelectedItems);

      if (selected) {
        // When selecting items, validate they're from the same site/date
        const itemsToAdd = cart.filter((item) => itemIds.includes(item.id));

        if (itemsToAdd.length > 0) {
          const firstItem = itemsToAdd[0];
          const siteCode = firstItem.options?.site_mapping_code;
          const date = firstItem.options?.date;

          // Check if any already selected item has different site or date
          const currentSelectedCartItems = cart.filter((item) => newSelectedItems.has(item.id));
          const conflictingItem = currentSelectedCartItems.find(
            (selectedItem) => selectedItem.options?.site_mapping_code !== siteCode || selectedItem.options?.date !== date,
          );

          if (conflictingItem && newSelectedItems.size > 0) {
            toast.error('Chỉ có thể chọn các dịch vụ cùng địa điểm và cùng ngày');
            return prevSelectedItems;
          }

          // Add all items in the batch
          itemIds.forEach((itemId) => newSelectedItems.add(itemId));
        }
      } else {
        // Remove all items in the batch
        itemIds.forEach((itemId) => newSelectedItems.delete(itemId));
      }

      return newSelectedItems;
    });
  };

  const handleItemRemove = async (itemId: string) => {
    setIsLoading(true);
    try {
      const item = cart.find((i) => i.id === itemId);
      if (item) {
        await removeFromCart({ rowId: item.rowId });
        // Remove from selected items if it was selected
        const newSelectedItems = new Set(selectedItems);
        newSelectedItems.delete(itemId);
        setSelectedItems(newSelectedItems);
        toast.success('Đã xóa dịch vụ khỏi giỏ hàng');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      toast.error('Không thể xóa dịch vụ. Vui lòng thử lại');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateQuantity = async (rowId: string, quantity: number) => {
    setIsLoading(true);
    try {
      const item = cart.find((i) => i.rowId === rowId);
      if (item) {
        await updateCartItem({ rowId, quantity });
        toast.success('Đã cập nhật số lượng dịch vụ');
      }
    } catch (error) {
      toast.error('Không thể cập nhật số lượng dịch vụ. Vui lòng thử lại');
    } finally {
      setIsLoading(false);
    }
  };

  const isNeedLockSlot = useMemo(() => {
    if (selectedCartItems.length === 0) return false;
    const hasInventories = selectedCartItems.filter((item) => item.options.has_inventory);
    return (
      hasInventories &&
      selectedCartItems.some(
        (item) =>
          (item.options.has_inventory && !item.options.reservation_code) ||
          (item.options.has_inventory && item.options.reservation_code && item.options.expired_at && !isPast(item.options.expired_at)),
      )
    );
  }, [selectedCartItems]);

  // Ungated checkout continuation. Kept separate from the auth gate so the
  // post-login resume can invoke it directly: right after login the stored
  // callback may run before React re-renders with the fresh `auth` prop, so
  // re-entering the gated wrapper would see stale `auth.user = null` and
  // re-open the dialog instead of proceeding.
  const proceedToCheckout = async () => {
    if (selectedItems.size === 0) {
      toast.error('Vui lòng chọn ít nhất một dịch vụ');
      return;
    }

    if (isNeedLockSlot) {
      try {
        const services = selectedCartItems
          .map((item) => {
            if (selectedSessions[item.rowId]) {
              return {
                row_id: item.rowId,
                session_id: selectedSessions[item.rowId].session_id,
                outlet_name: selectedSessions[item.rowId].outlet_name,
                start_time: selectedSessions[item.rowId].start_time,
              };
            }
            return null;
          })
          .filter((service) => !!service);
        if (services.length === 0) {
          toast.error('Vui lòng chọn session cho tất cả dịch vụ');
          return;
        }
        const response = await lockSlots({ services });
        window.dispatchEvent(new Event('cart-updated'));

        if (response.success) {
          setTimeout(() => {
            sessionStorage.setItem('checkout_items', JSON.stringify(selectedCartItems.map((item) => item.rowId)));
            router.visit('/checkout');
          }, 2000);
        } else {
          toast.error('Có lỗi xảy ra khi kiểm tra thông tin!');
        }
      } catch (error) {
        toast.error('Lỗi khi khóa slot');
        return;
      }
    } else {
      sessionStorage.setItem('checkout_items', JSON.stringify(selectedCartItems.map((item) => item.rowId)));
      router.visit('/checkout');
    }
  };

  const handleContinueToCheckout = async () => {
    // Checkout is login-required — gate before locking slots / navigating.
    if (!auth?.user) {
      requireLogin(proceedToCheckout);
      return;
    }

    await proceedToCheckout();
  };

  // Generate checkout steps with current step highlighted
  const checkoutSteps = generateCheckoutSteps('cart');

  return (
    <>
      <div className="container mx-auto max-w-7xl px-4 py-8">
        {cart.length === 0 ? (
          <CartEmptyState />
        ) : (
          <div className="grid grid-cols-1 gap-8 pb-12 md:pb-20 lg:grid-cols-3">
            {/* Left Column - Service Selection */}

            <div className="lg:col-span-2">
              <CheckoutProgress className="mb-12" steps={checkoutSteps} />
              <CartServiceSelector
                cart={cart}
                selectedItems={selectedItems}
                onItemSelectionChange={handleItemSelectionChange}
                onBatchSelectionChange={handleBatchSelectionChange}
                onItemRemove={handleItemRemove}
                onUpdateQuantity={handleUpdateQuantity}
                onContinueToCheckout={handleContinueToCheckout}
                isLoading={isLoading}
                isFetching={isFetching}
              />
            </div>

            {/* Right Column - Order Summary */}
            <div className="lg:col-span-1">
              <OrderSummaryCard
                selectedItems={selectedCartItems}
                total={selectedTotal}
                discount={selectedDiscount}
                onContinue={handleContinueToCheckout}
                continueText={`Tiếp tục (${selectedItems.size} dịch vụ)`}
                continueDisabled={selectedItems.size === 0}
                isLoading={isLoading || isFetching}
                step="cart"
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}
